import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  setPersistence,
  browserLocalPersistence,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { getAnalytics } from "firebase/analytics";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import { useState, useCallback, useEffect, useMemo } from "react";
import firebaseConfig from "./firebasecfg";
import { ADMIN_EMAILS } from "../adminEmails";

// Initialize Firebase
let app;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
  getAnalytics(app);
} else {
  app = getApps()[0];
}

const auth = getAuth(app);
const db = getFirestore(app);

// Set persistence immediately on initialization for mobile
setPersistence(auth, browserLocalPersistence).catch(() => {
  // Silently handle persistence errors
});

// Configure Google Auth Provider with necessary settings for mobile
const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  prompt: "select_account",
});

const getUserData = async (uid) => {
  const docRef = doc(db, "users", uid);
  const docSnap = await getDoc(docRef);
  return docSnap;
};

const useUser = () => {
  const [user, setUser] = useState(null);
  const [loggingIn, setLoggingIn] = useState(true);

  // Admin logic: check if user's email is in ADMIN_EMAILS
  const admin = useMemo(
    () => (user && user.email ? ADMIN_EMAILS.includes(user.email) : false),
    [user]
  );

  //check if user is already signed in
  useEffect(() => {
    let isSubscribed = true;

    // First, handle redirect result for mobile sign-in
    getRedirectResult(auth)
      .then(() => {
        // Redirect result handled silently
      })
      .catch(() => {
        if (isSubscribed) {
          setLoggingIn(false);
        }
      });

    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!isSubscribed) return;

      setLoggingIn(true);

      if (currentUser) {
        getUserData(currentUser.uid)
          .then((docSnap) => {
            if (!isSubscribed) return;
            let { uid, email, displayName, photoURL } = currentUser;
            let newUser = {
              uid,
              email,
              displayName,
              photoURL,
              ...(docSnap.exists() ? docSnap.data() : {}),
            };
            setLoggingIn(false);
            setUser(newUser);
          })
          .catch(() => {
            if (!isSubscribed) return;
            // Even if Firestore fails, set the user from auth data
            let { uid, email, displayName, photoURL } = currentUser;
            let newUser = { uid, email, displayName, photoURL };
            setLoggingIn(false);
            setUser(newUser);
          });
      } else {
        setUser(null);
        setLoggingIn(false);
      }
    });

    // Cleanup subscription on unmount
    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, []);

  const signIn = useCallback((callback, isSignUp = false) => {
    setLoggingIn(true);

    // Try popup first for BOTH mobile and desktop
    // Popup is more reliable for capturing auth result
    signInWithPopup(auth, provider)
      .then(async (result) => {
        const user = result.user;

        // Check if user exists in Firestore
        const docSnap = await getUserData(user.uid);

        if (!isSignUp && !docSnap.exists()) {
          // User is trying to login but doesn't have an account
          await signOut(auth);
          setLoggingIn(false);
          if (callback && typeof callback === "function") {
            callback({
              error: "No account found. Please sign up first.",
            });
          }
          return;
        }

        if (isSignUp && !docSnap.exists()) {
          // Create new user document in Firestore
          await setDoc(doc(db, "users", user.uid), {
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
            createdAt: serverTimestamp(),
          });
        }

        if (callback && typeof callback === "function") {
          callback({ success: true });
        }
        // onAuthStateChanged will handle setting the user
      })
      .catch((err) => {
        // If popup is blocked or fails on mobile, try redirect
        if (
          err.code === "auth/popup-blocked" ||
          err.code === "auth/popup-closed-by-user" ||
          err.code === "auth/cancelled-popup-request"
        ) {
          signInWithRedirect(auth, provider);
          return;
        }

        if (err.code === "auth/unauthorized-domain") {
          alert(
            "This domain is not authorized for sign-in. Please contact the administrator."
          );
        }

        if (callback && typeof callback === "function") {
          callback({
            error: err.message || "Error signing in",
          });
        }
        setLoggingIn(false);
      });
  }, []);

  const logout = useCallback(() => {
    signOut(auth).then(() => setUser(null));
  }, [setUser]);

  return { user, admin, signIn, logout, loggingIn };
};

export default useUser;
